// AdminHPCReportViewer.jsx
// Renders a pre-generated HPC report for viewing/downloading in the Admin panel.
// Receives report data as props (no self-fetching — data comes from admin HPC API).
// 100% same rendering as the Student panel HPCReportPage.
import React, { useRef, useState } from 'react';
import AssessmentIconSVG from './svg/AssessmentIconSVG';
import { exportToPdf } from './exportToPdf';
import './ReportStyles.css';

// Foundational report components
import {
    CoverPage1, CoverPage2, CoverPage3, CoverPage4,
    PartA1 as FoundationalPartA1,
    PartA2 as FoundationalPartA2,
    PartBDomainPage as FoundationalPartBDomainPage,
    PartC as FoundationalPartC,
    TeacherFeedbackPage as FoundationalTeacherFeedbackPage,
} from './foundational_report/report';

// Middle report components
import {
    CaregiverObservationPage as MiddleCaregiverObservationPage,
    MiddleCoverPage1, MiddleCoverPage2, MiddleCoverPage3, MiddleCoverPage4,
    PartA1 as MiddlePartA1,
    PartA2 as MiddlePartA2,
    PartA3 as MiddlePartA3,
    PartBDomainPage as MiddlePartBDomainPage,
    PartC as MiddlePartC,
    PeerFeedbackPage as MiddlePeerFeedbackPage,
    SelfAssessmentPage as MiddleSelfAssessmentPage,
    TeacherFeedbackPage as MiddleTeacherFeedbackPage,
} from './middle_report/report';

// Preparatory report components
import {
    CaregiverObservationPage1 as PreparatoryCaregiverObservationPage1,
    CaregiverObservationPage2 as PreparatoryCaregiverObservationPage2,
    PartA1 as PreparatoryPartA1,
    PartA2 as PreparatoryPartA2,
    PartBDomainPage as PreparatoryPartBDomainPage,
    PartC as PreparatoryPartC,
    PeerFeedbackPage as PreparatoryPeerFeedbackPage,
    PrepCoverPage1, PrepCoverPage2, PrepCoverPage3, PrepCoverPage4,
    SelfAssessmentPage as PreparatorySelfAssessmentPage,
    TeacherFeedbackPage as PreparatoryTeacherFeedbackPage,
} from './preparatory_report/report';

// Secondary report components
import {
    CoverPage1 as SecondaryCoverPage1,
    CoverPage2 as SecondaryCoverPage2,
    CoverPage3 as SecondaryCoverPage3,
    CoverPage4 as SecondaryCoverPage4,
    PartA1 as SecondaryPartA1,
    PartA2 as SecondaryPartA2,
    PartA3 as SecondaryPartA3,
    PartA4 as SecondaryPartA4,
    PartA5 as SecondaryPartA5,
    PartA6 as SecondaryPartA6,
    PartBPage1 as SecondaryPartBPage1,
    PartBPage2 as SecondaryPartBPage2,
    PartBPage3 as SecondaryPartBPage3,
    PartBPage4 as SecondaryPartBPage4,
    PartBPage5 as SecondaryPartBPage5,
    PartBPage6 as SecondaryPartBPage6,
    PartBPage7 as SecondaryPartBPage7,
    PartBPage8 as SecondaryPartBPage8,
    PartCPage1 as SecondaryPartCPage1,
    PartCPage2 as SecondaryPartCPage2,
    PartCPage3 as SecondaryPartCPage3,
    PartCPage4 as SecondaryPartCPage4,
    PartCPage5 as SecondaryPartCPage5,
    PartCPage6 as SecondaryPartCPage6,
    PartCPage7 as SecondaryPartCPage7,
    PartCPage8 as SecondaryPartCPage8,
    PartCPage9 as SecondaryPartCPage9,
    PartCPage10 as SecondaryPartCPage10,
    PartDPage1 as SecondaryPartDPage1,
    PartDPage2 as SecondaryPartDPage2,
    PartDPage3 as SecondaryPartDPage3,
    PartDPage4 as SecondaryPartDPage4,
    PartDPage5 as SecondaryPartDPage5,
    PartE as SecondaryPartE,
    PartFPage1 as SecondaryPartFPage1,
    PartFPage2 as SecondaryPartFPage2,
    CompetencyProfilePage1 as SecondaryCompetencyProfilePage1,
    CompetencyProfilePage2 as SecondaryCompetencyProfilePage2,
} from './secondary_report/report';

import { displayDate } from './services/adminHPCService';
import { API_BASE_URL } from '../../config';

/**
 * AdminHPCReportViewer
 *
 * Props:
 *  - reportData: The report_data object from the generated report
 *  - stage: "Foundational Stage" | "Preparatory Stage" | "Middle Stage" | "Secondary Stage"
 *  - studentImage: base64 student profile picture (optional)
 *  - schoolBranding: { school_logo_url, principal_signature_url, ... } (optional)
 *  - onClose: callback to close the viewer
 */
const AdminHPCReportViewer = ({ reportData, stage, studentImage, schoolBranding, onClose, embedded = false }) => {
    const [exportingPdf, setExportingPdf] = useState(false);
    const reportRef = useRef(null);

    if (!reportData) {
        return (
            <div className="p-8 text-center">
                <p className="text-gray-500">No report data available.</p>
                {onClose && (
                    <button onClick={onClose} className="mt-4 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded">
                        Go Back
                    </button>
                )}
            </div>
        );
    }

    // Format date of birth if present
    const processedData = { ...reportData };
    if (processedData.part_a_1 && processedData.part_a_1.date_of_birth) {
        processedData.part_a_1.date_of_birth = displayDate(processedData.part_a_1.date_of_birth);
    }

    const dataForPartA1 = processedData?.part_a_1 || null;
    const dataForPartA2 = processedData?.part_a_2 || null;
    const dataForPartA3 = processedData?.part_a_3 || null;
    const dataForPartA4 = processedData?.part_a_4 || null;
    const dataForPartA5 = processedData?.part_a_5 || null;
    const dataForPartA6 = processedData?.part_a_6 || null;
    const dataForPartB = processedData?.part_b || [];
    const dataForPartC = processedData?.part_c || [];
    const dataForPartD = processedData?.part_d || [];
    const dataForPartE = processedData?.part_e || [];
    const dataForPartF = processedData?.part_f || [];

    const isSecondary = stage === "Secondary Stage";
    const isPreparatory = stage === "Preparatory Stage";
    const isMiddle = stage === "Middle Stage";
    const isFoundational = stage === "Foundational Stage";

    const PartA1 = isMiddle ? MiddlePartA1 : (isPreparatory ? PreparatoryPartA1 : FoundationalPartA1);
    const PartA2 = isMiddle ? MiddlePartA2 : (isPreparatory ? PreparatoryPartA2 : FoundationalPartA2);
    const PartA3 = isMiddle ? MiddlePartA3 : () => null;
    const PartBDomainPage = isMiddle ? MiddlePartBDomainPage : (isPreparatory ? PreparatoryPartBDomainPage : FoundationalPartBDomainPage);
    const TeacherFeedbackPage = isMiddle ? MiddleTeacherFeedbackPage : (isPreparatory ? PreparatoryTeacherFeedbackPage : FoundationalTeacherFeedbackPage);
    const SelfAssessmentPage = isMiddle ? MiddleSelfAssessmentPage : (isPreparatory ? PreparatorySelfAssessmentPage : null);
    const PeerFeedbackPage = isMiddle ? MiddlePeerFeedbackPage : (isPreparatory ? PreparatoryPeerFeedbackPage : null);
    const PartC = isMiddle ? MiddlePartC : (isPreparatory ? PreparatoryPartC : FoundationalPartC);

    const handleExportPdf = async () => {
        if (!reportRef.current) return;
        setExportingPdf(true);
        try {
            const stagePrefix = isSecondary ? "Secondary_" : (isMiddle ? "Middle_" : (isPreparatory ? "Preparatory_" : "Foundational_"));
            await exportToPdf(
                'admin-hpc-report-content',
                `${stagePrefix}HPC_Report_${dataForPartA1?.student_name?.replace(/\s/g, '_') || 'Student'}.pdf`
            );
        } catch (error) {
            console.error("Error exporting PDF:", error);
            alert("There was an error generating the PDF. Please try again.");
        } finally {
            setExportingPdf(false);
        }
    };

    return (
        <div className={"hpc-report-viewer py-8 bg-gray-100 min-h-screen font-[poppins]" + (embedded ? " hpc-report-embedded" : "")} style={{ padding: embedded ? '0' : '2rem 0' }}>
            {/* Shared shell is used inside Student Reports Centre; existing workspace keeps its original compact bar. */}
            {embedded ? (
                <div className="hpc-embedded-toolbar no-print">
                    <div>
                        <span>Official student report</span>
                        <strong>{isSecondary ? "Secondary Stage" : isMiddle ? "Middle Stage" : isPreparatory ? "Preparatory Stage" : "Foundational Stage"} Performance Report</strong>
                    </div>
                    <div className="hpc-embedded-actions">
                        <button onClick={handleExportPdf} disabled={exportingPdf} className="hpc-embedded-primary">{exportingPdf ? 'Preparing PDF…' : 'Download official PDF'}</button>
                        {onClose && <button onClick={onClose} className="hpc-embedded-secondary">Close</button>}
                    </div>
                </div>
            ) : (
                <div className="max-w-[210mm] mx-auto mb-8 flex justify-between items-center bg-white p-4 rounded-lg shadow-lg">
                    <div className="flex items-center">
                        {onClose && (
                            <button onClick={onClose} className="mr-3 text-gray-500 hover:text-gray-700 text-xl font-bold">
                                ←
                            </button>
                        )}
                        <div className="bg-orange-600 text-white p-2 rounded-full mr-3">
                            <AssessmentIconSVG type="books" size={24} selected={true} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                {isSecondary ? "Secondary Stage" : isMiddle ? "Middle Stage" : isPreparatory ? "Preparatory Stage" : "Foundational Stage"} Performance Report
                            </h2>
                            <p className="text-gray-600 text-sm">
                                Student: <span className="font-semibold">{dataForPartA1?.student_name || 'Student'}</span>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleExportPdf}
                        disabled={exportingPdf}
                        className="no-print bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded shadow-md flex items-center disabled:opacity-50"
                    >
                        <AssessmentIconSVG type="computer" size={18} selected={true} />
                        <span className="ml-2">{exportingPdf ? 'Generating...' : 'Export as PDF'}</span>
                    </button>
                </div>
            )}

            {/* Report content */}
            <div id="admin-hpc-report-content" ref={reportRef} style={{ display: 'block', margin: '0 auto', padding: '0', maxWidth: '210mm' }}>

                {/* School Logo Page (branding front page) */}
                {schoolBranding?.school_logo_url && (
                    <div style={{ marginBottom: '2rem' }}>
                        <div className="report-page" style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            padding: '30mm 25mm',
                            minHeight: '297mm'
                        }}>
                            {/* Logo - Centered */}
                            <div style={{ marginBottom: '15mm', textAlign: 'center' }}>
                                <img 
                                    src={schoolBranding.school_logo_url.startsWith('http') ? schoolBranding.school_logo_url : `${API_BASE_URL}${schoolBranding.school_logo_url}`}
                                    alt="School Logo" 
                                    style={{ 
                                        maxWidth: '70mm', 
                                        maxHeight: '70mm', 
                                        objectFit: 'contain',
                                        display: 'block',
                                        margin: '0 auto'
                                    }} 
                                />
                            </div>

                            {/* School Name - Centered */}
                            {schoolBranding.school_name && (
                                <h1 style={{ 
                                    fontSize: '28pt', 
                                    fontWeight: 'bold', 
                                    color: '#1a1a1a', 
                                    margin: '0 0 6mm 0', 
                                    textAlign: 'center', 
                                    fontFamily: 'Poppins, sans-serif',
                                    lineHeight: '1.3'
                                }}>
                                    {schoolBranding.school_name}
                                </h1>
                            )}

                            {/* Address - Centered */}
                            {schoolBranding.school_address && (
                                <p style={{ 
                                    fontSize: '13pt', 
                                    color: '#666', 
                                    margin: '0 0 15mm 0', 
                                    textAlign: 'center', 
                                    lineHeight: '1.6',
                                    maxWidth: '140mm'
                                }}>
                                    {schoolBranding.school_address}
                                </p>
                            )}

                            {/* Divider - Centered */}
                            <div style={{ 
                                width: '70mm', 
                                height: '2px', 
                                backgroundColor: '#F97316', 
                                margin: '0 auto 15mm auto'
                            }}></div>

                            {/* HPC Title - Centered */}
                            <h2 style={{ 
                                fontSize: '24pt', 
                                fontWeight: 'bold', 
                                color: '#F97316', 
                                margin: '0 0 10mm 0', 
                                textAlign: 'center', 
                                fontFamily: 'Poppins, sans-serif'
                            }}>
                                Holistic Progress Card
                            </h2>

                            {/* Academic Year - Centered */}
                            {schoolBranding.academic_year && (
                                <p style={{ 
                                    fontSize: '16pt', 
                                    fontWeight: '600', 
                                    color: '#444', 
                                    margin: 0, 
                                    textAlign: 'center'
                                }}>
                                    Academic Year: {schoolBranding.academic_year}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Cover Pages */}
                {isFoundational && (
                    <>
                        <div style={{ marginBottom: '2rem' }}><CoverPage1 /></div>
                        <div style={{ marginBottom: '2rem' }}><CoverPage2 /></div>
                        <div style={{ marginBottom: '2rem' }}><CoverPage3 /></div>
                        <div style={{ marginBottom: '2rem' }}><CoverPage4 /></div>
                    </>
                )}
                {isMiddle && (
                    <>
                        <div style={{ marginBottom: '2rem' }}><MiddleCoverPage1 /></div>
                        <div style={{ marginBottom: '2rem' }}><MiddleCoverPage2 /></div>
                        <div style={{ marginBottom: '2rem' }}><MiddleCoverPage3 /></div>
                        <div style={{ marginBottom: '2rem' }}><MiddleCoverPage4 /></div>
                    </>
                )}
                {isPreparatory && (
                    <>
                        <div style={{ marginBottom: '2rem' }}><PrepCoverPage1 /></div>
                        <div style={{ marginBottom: '2rem' }}><PrepCoverPage2 /></div>
                        <div style={{ marginBottom: '2rem' }}><PrepCoverPage3 /></div>
                        <div style={{ marginBottom: '2rem' }}><PrepCoverPage4 /></div>
                    </>
                )}
                {isSecondary && (
                    <>
                        <div style={{ marginBottom: '2rem' }}><SecondaryCoverPage1 /></div>
                        <div style={{ marginBottom: '2rem' }}><SecondaryCoverPage2 /></div>
                        <div style={{ marginBottom: '2rem' }}><SecondaryCoverPage3 /></div>
                        <div style={{ marginBottom: '2rem' }}><SecondaryCoverPage4 /></div>
                    </>
                )}

                {/* Part A1 */}
                {dataForPartA1 && (
                    <div style={{ marginBottom: '2rem' }}>
                        {isSecondary ? (
                            <SecondaryPartA1 data={dataForPartA1} studentImage={studentImage} />
                        ) : (
                            <PartA1 data={dataForPartA1} studentImage={studentImage} />
                        )}
                    </div>
                )}

                {/* Secondary Stage Parts A2-A6 */}
                {isSecondary && (
                    <>
                        {dataForPartA2 && (
                            <>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA2 data={dataForPartA2} pageNumber={1} /></div>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA2 data={dataForPartA2} pageNumber={2} /></div>
                            </>
                        )}
                        {dataForPartA3 && (
                            <>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA3 data={dataForPartA3} pageNumber={1} /></div>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA3 data={dataForPartA3} pageNumber={2} /></div>
                            </>
                        )}
                        {dataForPartA4 && (
                            <>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA4 data={dataForPartA4} pageNumber={1} /></div>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA4 data={dataForPartA4} pageNumber={2} /></div>
                            </>
                        )}
                        {dataForPartA5 && (
                            <div style={{ marginBottom: '2rem' }}><SecondaryPartA5 data={dataForPartA5} /></div>
                        )}
                        {dataForPartA6 && (
                            <>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA6 data={dataForPartA6} pageNumber={1} /></div>
                                <div style={{ marginBottom: '2rem' }}><SecondaryPartA6 data={dataForPartA6} pageNumber={2} /></div>
                            </>
                        )}
                    </>
                )}

                {/* Other Stages Parts A2-A3 */}
                {!isSecondary && dataForPartA2 && (
                    <div style={{ marginBottom: '2rem' }}><PartA2 data={dataForPartA2} /></div>
                )}
                {isMiddle && dataForPartA3 && (
                    <div style={{ marginBottom: '2rem' }}><PartA3 data={dataForPartA3} /></div>
                )}

                {/* Part B - Domain Pages */}
                {isSecondary ? (
                    dataForPartB.length > 0 ? (
                        dataForPartB.map((domainEntry) => (
                            domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                domainEntry.assignments.map((assignment) => (
                                    <React.Fragment key={`${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage1 data={assignment.part_b?.page_1} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage2 data={assignment.part_b?.page_2} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage3 data={assignment.part_b?.page_3} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage4 data={assignment.part_b?.page_4} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage5 data={assignment.part_b?.page_5} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage6 data={assignment.part_b?.page_6} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage7 data={assignment.part_b} /></div>
                                        <div style={{ marginBottom: '2rem' }}><SecondaryPartBPage8 data={assignment.part_b} /></div>
                                    </React.Fragment>
                                ))
                            ) : null
                        ))
                    ) : (
                        <div style={{ marginBottom: '2rem', padding: '40px', textAlign: 'center', backgroundColor: '#f9f9f9', border: '2px dashed #ccc', borderRadius: '8px' }}>
                            <h3 style={{ color: '#666', marginBottom: '10px' }}>No Part B Activities</h3>
                            <p style={{ color: '#999' }}>No domain activities were selected for this report.</p>
                        </div>
                    )
                ) : (
                    dataForPartB.map((domainEntry) => (
                        domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                            domainEntry.assignments.map((assignment) => (
                                <React.Fragment key={`${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <PartBDomainPage domain={domainEntry} assignment={assignment} />
                                    </div>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <TeacherFeedbackPage
                                            assignment={assignment}
                                            proficiencyMarks={assignment.proficiency_marks}
                                            observationalNotes={assignment.observational_notes || assignment.teacher_feedback}
                                        />
                                    </div>
                                    {(isPreparatory || isMiddle) && (
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SelfAssessmentPage assignment={assignment} />
                                        </div>
                                    )}
                                    {(isPreparatory || isMiddle) && (
                                        <div style={{ marginBottom: '2rem' }}>
                                            <PeerFeedbackPage assignment={assignment} />
                                        </div>
                                    )}
                                    {isPreparatory && (
                                        <>
                                            <div style={{ marginBottom: '2rem' }}>
                                                <PreparatoryCaregiverObservationPage1 assignment={assignment} />
                                            </div>
                                            <div style={{ marginBottom: '2rem' }}>
                                                <PreparatoryCaregiverObservationPage2 assignment={assignment} />
                                            </div>
                                        </>
                                    )}
                                    {isMiddle && (
                                        <div style={{ marginBottom: '2rem' }}>
                                            <MiddleCaregiverObservationPage assignment={assignment} />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))
                        ) : null
                    ))
                )}

                {/* Part C */}
                {!isSecondary && dataForPartC && dataForPartC.length > 0 && (
                    <div style={{ marginBottom: '2rem' }}><PartC data={dataForPartC} /></div>
                )}
                {isSecondary && dataForPartC.length > 0 && (
                    dataForPartC.map((domainEntry) => (
                        domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                            domainEntry.assignments.map((assignment) => (
                                <React.Fragment key={`part_c_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage1 data={assignment.part_c?.page_1} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage2 data={assignment.part_c?.page_2} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage3 data={assignment.part_c?.page_3} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage4 data={assignment.part_c?.page_4} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage5 data={assignment.part_c?.page_5} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage6 data={assignment.part_c?.page_6} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage7 data={assignment.part_c?.page_8} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage8 data={assignment.part_c?.page_7} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage9 data={assignment.part_c?.page_9} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartCPage10 data={assignment.part_c?.page_10} /></div>
                                </React.Fragment>
                            ))
                        ) : null
                    ))
                )}

                {/* Part D */}
                {isSecondary && dataForPartD.length > 0 && (
                    dataForPartD.map((domainEntry) => (
                        domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                            domainEntry.assignments.map((assignment) => (
                                <React.Fragment key={`part_d_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartDPage1 data={assignment.part_d?.page_1} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartDPage2 data={assignment.part_d?.page_2} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartDPage3 data={assignment.part_d?.page_3} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartDPage4 data={assignment.part_d?.page_4} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartDPage5 data={assignment.part_d?.page_5} /></div>
                                </React.Fragment>
                            ))
                        ) : null
                    ))
                )}

                {/* Part E */}
                {isSecondary && dataForPartE.length > 0 && (
                    dataForPartE.map((domainEntry) => (
                        domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                            domainEntry.assignments.map((assignment) => (
                                <React.Fragment key={`part_e_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartE data={assignment.part_e?.student_fields} /></div>
                                </React.Fragment>
                            ))
                        ) : null
                    ))
                )}

                {/* Part F */}
                {isSecondary && dataForPartF.length > 0 && (
                    dataForPartF.map((domainEntry) => (
                        domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                            domainEntry.assignments.map((assignment) => (
                                <React.Fragment key={`part_f_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartFPage1 data={assignment.part_f?.page_1} /></div>
                                    <div style={{ marginBottom: '2rem' }}><SecondaryPartFPage2 data={assignment.part_f?.page_2} /></div>
                                    {assignment.competency_profile && (
                                        <>
                                            <div style={{ marginBottom: '2rem' }}><SecondaryCompetencyProfilePage1 data={assignment.competency_profile?.page_1} /></div>
                                            <div style={{ marginBottom: '2rem' }}><SecondaryCompetencyProfilePage2 data={assignment.competency_profile?.page_1} /></div>
                                        </>
                                    )}
                                </React.Fragment>
                            ))
                        ) : null
                    ))
                )}

                {/* Principal Signature Page (branding end page) */}
                {schoolBranding?.principal_signature_url && (
                    <div style={{ marginBottom: '2rem' }}>
                        <div className="report-page" style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            padding: '25mm 30mm 20mm 30mm',
                            minHeight: '297mm'
                        }}>
                            {/* Spacer - pushes signature to lower portion */}
                            <div style={{ flex: 1 }}></div>

                            {/* Signature block - right aligned */}
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                marginBottom: '35mm',
                                paddingRight: '10mm'
                            }}>
                                <div style={{ textAlign: 'center', minWidth: '70mm' }}>
                                    {/* Signature Image */}
                                    <img
                                        src={schoolBranding.principal_signature_url.startsWith('http') ? schoolBranding.principal_signature_url : `${API_BASE_URL}${schoolBranding.principal_signature_url}`}
                                        alt="Principal Signature"
                                        style={{ 
                                            maxWidth: '60mm', 
                                            maxHeight: '25mm', 
                                            objectFit: 'contain', 
                                            display: 'block', 
                                            margin: '0 auto 4mm auto' 
                                        }}
                                    />
                                    {/* Signature Line and Text */}
                                    <div style={{ 
                                        borderTop: '2px solid #333', 
                                        paddingTop: '4mm',
                                        width: '70mm',
                                        margin: '0 auto'
                                    }}>
                                        <p style={{ 
                                            fontSize: '13pt', 
                                            fontWeight: 'bold', 
                                            color: '#1a1a1a', 
                                            margin: '0 0 2mm 0', 
                                            fontFamily: 'Poppins, sans-serif',
                                            textAlign: 'center'
                                        }}>
                                            {schoolBranding.principal_name || 'Principal'}
                                        </p>
                                        {schoolBranding.principal_designation && (
                                            <p style={{ 
                                                fontSize: '11pt', 
                                                color: '#666', 
                                                margin: 0,
                                                textAlign: 'center'
                                            }}>
                                                {schoolBranding.principal_designation}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* School footer - centered */}
                            <div style={{ 
                                textAlign: 'center', 
                                borderTop: '1.5px solid #ddd', 
                                paddingTop: '8mm',
                                marginTop: 'auto'
                            }}>
                                {schoolBranding.school_name && (
                                    <p style={{ 
                                        fontSize: '12pt', 
                                        fontWeight: '600', 
                                        color: '#333', 
                                        margin: '0 0 2mm 0', 
                                        fontFamily: 'Poppins, sans-serif'
                                    }}>
                                        {schoolBranding.school_name}
                                    </p>
                                )}
                                {schoolBranding.school_address && (
                                    <p style={{ 
                                        fontSize: '10pt', 
                                        color: '#777', 
                                        margin: 0,
                                        lineHeight: '1.4'
                                    }}>
                                        {schoolBranding.school_address}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminHPCReportViewer;
